import React, { useEffect, useState } from "react";
import { collection, doc, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { firestore } from "../helpers/firebaseConfig";
import "./LiveTicketsPage.css";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
];
const ticketsPath = (year) => ["Feedback", "builder_help", String(year)];
const PAGE_SIZE = 50;

const dateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const recentDates = Array.from({ length: 30 }, (_, offset) => {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() - offset);
  return dateKey(date);
});
const asDate = (value) => value?.toDate?.() || (value?.seconds ? new Date(value.seconds * 1000) : null);
const ticketFromDoc = (snapshot, year) => ({
  id: snapshot.id,
  year,
  ...snapshot.data(),
  createdAtDate: asDate(snapshot.data().createdAt),
});
const ticketTime = (ticket) => ticket.createdAtDate?.getTime?.() || 0;

export default function LiveTicketsPage() {
  const [selectedDates, setSelectedDates] = useState([]);
  const [page, setPage] = useState(1);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [savingId, setSavingId] = useState("");

  useEffect(() => {
    setLoading(true);
    setTickets([]);
    setLoadError("");
    const years = selectedDates.length
      ? [...new Set(selectedDates.map((date) => date.slice(0, 4)))]
      : [String(new Date().getFullYear()), String(new Date().getFullYear() - 1)];
    const results = new Map();
    const unsubscribe = years.map((year) => {
      const dates = selectedDates.filter((date) => date.startsWith(`${year}-`));
      const yearTickets = collection(firestore, ...ticketsPath(year));
      const ticketQuery = dates.length === 0
        ? query(yearTickets, orderBy("createdAt", "desc"), limit(200))
        : query(yearTickets, where("dateKey", dates.length === 1 ? "==" : "in", dates.length === 1 ? dates[0] : dates));
      return onSnapshot(ticketQuery, (snapshot) => {
        results.set(year, snapshot.docs.map((item) => ticketFromDoc(item, year)));
        setTickets([...results.values()].flat().sort((a, b) => ticketTime(b) - ticketTime(a)));
        if (results.size === years.length) setLoading(false);
      }, () => {
        setLoadError("Could not load tickets. Check your Firestore access and try again.");
        setLoading(false);
      });
    });
    return () => unsubscribe.forEach((stop) => stop());
  }, [selectedDates]);
  const pageCount = Math.max(1, Math.ceil(tickets.length / PAGE_SIZE));
  const visibleTickets = tickets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectDate = (date) => {
    setPage(1);
    setSelectedDates((current) => current.includes(date) ? current.filter((item) => item !== date) : [...current, date].sort().reverse());
  };

  const changeStatus = async (ticket, status) => {
    setSavingId(`${ticket.year}:${ticket.id}`);
    setSaveError("");
    try {
      await updateDoc(doc(firestore, ...ticketsPath(ticket.year), ticket.id), { status, statusUpdatedAt: serverTimestamp() });
    } catch {
      setSaveError("Status was not saved. Please try again.");
    } finally {
      setSavingId("");
    }
  };

  return <main className="live-ticketsPage">
    <div className="live-ticketsHeader">
      <div><p className="live-ticketsEyebrow">Builder support</p><h1>Live tickets</h1><p>Requests from the agent’s Help button, newest first.</p></div>
    </div>
    <div className="live-ticketsFilters" aria-label="Filter tickets by date">
      <details className="live-ticketsDateMenu">
        <summary>{selectedDates.length ? `${selectedDates.length} date${selectedDates.length === 1 ? "" : "s"} selected` : "All recent dates"}<span aria-hidden="true">▾</span></summary>
        <div className="live-ticketsDateOptions">
          <button type="button" onClick={() => { setSelectedDates([]); setPage(1); }}>All recent dates</button>
          {Array.from(new Set([...selectedDates, ...recentDates])).sort().reverse().map((date, index) => <label key={date}><input type="checkbox" checked={selectedDates.includes(date)} disabled={!selectedDates.includes(date) && selectedDates.length >= 30} onChange={() => selectDate(date)} /><span>{index === 0 && date === recentDates[0] ? "Today" : date === recentDates[1] ? "Yesterday" : new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span></label>)}
        </div>
      </details>
      <label className="live-ticketsAddDate">Add older date <input type="date" value="" onChange={(event) => { if (event.target.value && !selectedDates.includes(event.target.value) && selectedDates.length < 30) selectDate(event.target.value); }} /></label>
    </div>
    {(loadError || saveError) && <div className="live-ticketsError" role="alert">{[loadError, saveError].filter(Boolean).join(" ")}</div>}
    <div className="live-ticketsCount">{loading ? "Loading tickets…" : `${tickets.length} ticket${tickets.length === 1 ? "" : "s"}`}</div>
    {!loading && tickets.length === 0 && !loadError && <div className="live-ticketsEmpty">No tickets for these dates.</div>}
    <div className="live-ticketsList">
      {visibleTickets.map((ticket) => {
        const key = `${ticket.year}:${ticket.id}`;
        return <article className="live-ticket" key={key}>
          <header className="live-ticketMain">
            <div className="live-ticketMeta"><strong>{ticket.email || ticket.AccountHolder || "Unknown account"}</strong><span aria-hidden="true">·</span><time>{ticket.createdAtDate ? ticket.createdAtDate.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ticket.dateKey || ticket.year || "Date unavailable"}</time></div>
            <label>Status <select value={STATUS_OPTIONS.some((option) => option.value === ticket.status) ? ticket.status : "new"} disabled={savingId === key} onChange={(event) => changeStatus(ticket, event.target.value)}>{STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          </header>
          <p className="live-ticketMessage">{ticket.Details || ticket.details || "No details provided."}</p>
          {(ticket.builderUrl || ticket.chatId) && <details className="live-ticketContext"><summary>More details</summary><dl>
            {ticket.builderUrl && <div><dt>Builder URL</dt><dd>{/^https?:\/\//i.test(ticket.builderUrl) ? <a href={ticket.builderUrl} target="_blank" rel="noopener noreferrer">{ticket.builderUrl}</a> : <code>{ticket.builderUrl}</code>}</dd></div>}
            {ticket.chatId && <div><dt>Chat ID</dt><dd><code>{ticket.chatId}</code></dd></div>}
          </dl></details>}
        </article>;
      })}
    </div>
    {pageCount > 1 && <nav className="live-ticketsPagination" aria-label="Ticket pages"><button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)}>Previous</button><span>Page {page} of {pageCount}</span><button type="button" disabled={page === pageCount} onClick={() => setPage((current) => current + 1)}>Next</button></nav>}
  </main>;
}
